import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { formatDate, formatNumber } from '@/lib/format'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  ReadonlyValue,
  RowMenu,
  SearchBox,
  SkeletonTable,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import type { OfdCode, TaxRate, TaxRateInput, TaxRateKind } from './api'
import { createTaxRate, deleteTaxRate, fetchTaxRates, updateTaxRate } from './api'

/**
 * Справочник налоговых ставок (V57): НДС кодом с периодами действия.
 *
 * Повод — основная ставка 22 % с 01.01.2026: код `vat_standard` живёт двумя периодами,
 * и товар, хранящий код, сам переходит на 22 % первого января. Экран показывает периоды
 * строками и правила справочника словами, но держит их сервер:
 *
 * - **периоды одного кода не пересекаются** — иначе на одну дату у кода две ставки;
 * - **у ставки, по которой есть документы, не меняются процент, вид, признак чека и
 *   начало** — только название и дата окончания: процент лежит в строках документов
 *   снимком, и правка задним числом поменяла бы их смысл. Ставку закрывают датой и
 *   заводят новый период («Новый период» в меню строки);
 * - **удалить нельзя** ставку со строками документов и последний период кода, который
 *   указан у товаров. Пункт меню при этом виден с причиной, а не спрятан.
 *
 * Признак ставки в чеке (ФФД, тег 1199) выбирается из перечня сервера, а не вводится:
 * список задан форматом фискальных данных, и подходящие варианты сужаются по проценту и
 * расчётности — сервер всё равно отвергнет несовпадение.
 */
export function TaxRatesPage() {
  const { t, locale } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const rates = useQuery({ queryKey: ['admin-tax-rates'], queryFn: ({ signal }) => fetchTaxRates(signal) })
  const [draft, setDraft] = useState<TaxDraft | null>(null)
  const [removing, setRemoving] = useState<TaxRate | null>(null)
  const [query, setQuery] = useState('')

  const list = rates.data?.rates ?? []
  const needle = query.trim().toLowerCase()
  const shown = list.filter((rate) => !needle || [rate.code, rate.name, rate.ofdCode].some((value) => value.toLowerCase().includes(needle)))
  const forbidden = isForbidden(rates.error)
  const today = isoToday()

  const remove = useMutation({
    mutationFn: (id: string) => deleteTaxRate(id),
    onSuccess: () => {
      setRemoving(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-tax-rates'] })
    },
    onError: (error) => {
      setRemoving(null)
      editing.fail(error)
    },
  })

  const periodsOf = (code: string) => list.filter((rate) => rate.code === code).length

  return (
    <AdminPage
      titleKey="nav.adminTaxRates"
      count={rates.data ? (needle ? t('adm.ofTotal', { shown: shown.length, total: list.length }) : list.length.toLocaleString(locale)) : undefined}
      leadKey="adm.tax.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.tax.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && <PrimaryButton label={t('adm.tax.add')} onClick={() => setDraft(newDraft())} />}
    >
      {!forbidden && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('adm.tax.search')} />
        </div>
      )}
      <Notice text={editing.notice} />
      <AdminState
        isLoading={rates.isLoading}
        error={rates.error}
        onRetry={() => void rates.refetch()}
        forbiddenTitleKey="adm.tax.forbidden"
        errorTitleKey="adm.tax.error"
        skeleton={<SkeletonTable columns={8} rows={8} />}
      >
        {list.length === 0 ? (
          <StateView boxed kind="setup" title={t('adm.tax.setup')} description={t('adm.tax.setup.text')} />
        ) : shown.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.tax.empty', { query })}
                     actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>{t('adm.resetSearch')}</button>} />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('nav.adminTaxRates')}>
              <thead>
                <tr>
                  <th>{t('adm.tax.code')}</th>
                  <th>{t('adm.tax.name')}</th>
                  <th>{t('adm.tax.kind')}</th>
                  <th className="num">{t('adm.tax.percent')}</th>
                  <th>{t('adm.tax.ofd')}</th>
                  <th>{t('adm.tax.period')}</th>
                  <th>{t('adm.tax.state')}</th>
                  <th className="num">{t('adm.tax.usage')}</th>
                  {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((rate) => {
                  const state = stateOf(rate, today)
                  const items: MenuAction[] = [
                    { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setDraft(editDraft(rate)) },
                    { id: 'period', label: t('adm.tax.newPeriod'), icon: 'plus', onSelect: () => setDraft(periodDraft(rate)) },
                    {
                      id: 'delete',
                      label: t('adm.tax.delete'),
                      icon: 'trash',
                      danger: true,
                      onSelect: () => setRemoving(rate),
                      disabledReason: rate.documentLines > 0
                        ? t('adm.tax.deleteBlockedLines', { count: rate.documentLines })
                        : rate.products > 0 && periodsOf(rate.code) === 1
                          ? t('adm.tax.deleteBlockedProducts', { count: rate.products })
                          : undefined,
                    },
                  ]
                  return (
                    <tr key={rate.id} className={draft?.id === rate.id ? 'is-selected' : undefined}>
                      <td><span className="mono strong">{rate.code}</span></td>
                      <td>{rate.name}</td>
                      <td>{t(`adm.tax.kind.${rate.kind}`)}</td>
                      <td className="num">
                        {rate.estimated ? `${formatNumber(rate.percent, locale)}/${formatNumber(100 + Number(rate.percent), locale)}` : `${formatNumber(rate.percent, locale)} %`}
                        {rate.estimated && <span className="badge" style={{ marginLeft: 6 }}>{t('adm.tax.estimatedShort')}</span>}
                      </td>
                      <td><span className="mono muted">{rate.ofdCode}</span></td>
                      <td>
                        {formatDate(rate.validFrom, locale)} — {rate.validTo ? formatDate(rate.validTo, locale) : t('adm.tax.openEnded')}
                      </td>
                      <td>
                        <span className={state === 'active' ? 'badge b-success' : state === 'future' ? 'badge b-info' : 'badge'}>
                          <span className="dot" aria-hidden="true" />
                          {t(`adm.tax.state.${state}`)}
                        </span>
                      </td>
                      <td className="num">
                        <span title={t('adm.tax.usage.hint')}>
                          {t('adm.tax.usage.value', { lines: rate.documentLines, products: rate.products })}
                        </span>
                      </td>
                      {editing.mayEdit && (
                        <td className="adm-act">
                          <RowMenu label={t('adm.tax.actions', { code: rate.code })} items={items} />
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminState>
      {draft && rates.data && (
        <TaxSheet draft={draft} ofdCodes={rates.data.ofdCodes} kinds={rates.data.kinds}
                  onChange={setDraft} onClose={() => setDraft(null)} />
      )}
      <ConfirmModal
        open={removing != null}
        title={t('adm.tax.deleteConfirm', { name: removing?.name ?? '' })}
        confirmLabel={t('adm.tax.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p>{t('adm.tax.deleteConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

type TaxState = 'active' | 'future' | 'closed'

function stateOf(rate: TaxRate, today: string): TaxState {
  if (rate.validFrom > today) return 'future'
  if (rate.validTo != null && rate.validTo < today) return 'closed'
  return 'active'
}

/** Сегодня по местному календарю — даты справочника сравниваются строками ГГГГ-ММ-ДД. */
function isoToday(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

interface TaxDraft {
  /** null — новая ставка или новый период. */
  id: string | null
  /** Код зафиксирован: правка и новый период того же кода. */
  codeLocked: boolean
  /** По ставке есть документы — меняются только название и окончание. */
  usedLines: number
  code: string
  name: string
  kind: TaxRateKind
  percent: string
  estimated: boolean
  ofdCode: string
  validFrom: string
  validTo: string
}

function newDraft(): TaxDraft {
  return { id: null, codeLocked: false, usedLines: 0, code: '', name: '', kind: 'standard', percent: '', estimated: false, ofdCode: '', validFrom: isoToday(), validTo: '' }
}

function editDraft(rate: TaxRate): TaxDraft {
  return {
    id: rate.id,
    codeLocked: true,
    usedLines: rate.documentLines,
    code: rate.code,
    name: rate.name,
    kind: rate.kind,
    percent: String(rate.percent),
    estimated: rate.estimated,
    ofdCode: rate.ofdCode,
    validFrom: rate.validFrom,
    validTo: rate.validTo ?? '',
  }
}

/** Новый период того же кода: начинается на следующий день после окончания этого. */
function periodDraft(rate: TaxRate): TaxDraft {
  return {
    ...editDraft(rate),
    id: null,
    usedLines: 0,
    percent: '',
    ofdCode: '',
    validFrom: rate.validTo ? nextDay(rate.validTo) : '',
    validTo: '',
  }
}

function nextDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1))
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10)
}

const CODE = /^[a-z][a-z0-9_]{0,31}$/

/** Признаки чека, подходящие к проценту, расчётности и виду ставки. */
export function matchingOfdCodes(codes: OfdCode[], percent: number, estimated: boolean, kind: TaxRateKind): OfdCode[] {
  return codes.filter((code) =>
    Number(code.percent) === percent && code.estimated === estimated && (kind === 'exempt') === (code.code === 'none'),
  )
}

function TaxSheet({ draft, ofdCodes, kinds, onChange, onClose }: {
  draft: TaxDraft
  ofdCodes: OfdCode[]
  kinds: TaxRateKind[]
  onChange: (draft: TaxDraft) => void
  onClose: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const frozen = draft.id != null && draft.usedLines > 0
  const percent = draft.percent.trim() === '' ? NaN : Number(draft.percent.replace(',', '.'))
  const matching = Number.isFinite(percent) ? matchingOfdCodes(ofdCodes, percent, draft.estimated, draft.kind) : []
  const codeError = !draft.codeLocked && draft.code !== '' && !CODE.test(draft.code) ? t('adm.tax.codeInvalid') : null
  const periodError = draft.validTo !== '' && draft.validFrom !== '' && draft.validTo < draft.validFrom ? t('adm.tax.periodInvalid') : null
  const incomplete = !CODE.test(draft.code) || draft.name.trim() === '' || !Number.isFinite(percent)
    || draft.ofdCode === '' || draft.validFrom === '' || periodError != null

  // Изменение процента, расчётности или вида сбрасывает признак, если он перестал подходить:
  // оставленный старым, он ушёл бы на сервер заведомым отказом.
  const update = (patch: Partial<TaxDraft>) => {
    const next = { ...draft, ...patch }
    const nextPercent = Number(next.percent.replace(',', '.'))
    const fits = Number.isFinite(nextPercent) ? matchingOfdCodes(ofdCodes, nextPercent, next.estimated, next.kind) : []
    if (!fits.some((code) => code.code === next.ofdCode)) {
      next.ofdCode = fits.length === 1 ? fits[0]!.code : ''
    }
    onChange(next)
  }

  const save = useMutation({
    mutationFn: () => {
      const body: TaxRateInput = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        kind: draft.kind,
        percent,
        estimated: draft.estimated,
        ofdCode: draft.ofdCode,
        validFrom: draft.validFrom,
        validTo: draft.validTo || null,
      }
      return draft.id ? updateTaxRate(draft.id, body) : createTaxRate(body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-tax-rates'] })
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  const title = draft.id ? `${draft.code} — ${draft.name}` : draft.codeLocked ? t('adm.tax.newPeriodOf', { code: draft.code }) : t('adm.tax.new')

  return (
    <EditLayer
      kind="sheet"
      overline={t('adm.tax.form')}
      title={title}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={draft.id ? t('admin.save') : t('adm.tax.add')}
                            pending={save.isPending} disabled={incomplete} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('adm.tax.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        {frozen && (
          <div className="alert alert-warning" role="note">
            <Icon name="lock" />
            <span>{t('adm.tax.frozen', { count: draft.usedLines })}</span>
          </div>
        )}
        <div className="form-grid">
          {draft.codeLocked ? (
            <Field label={t('adm.tax.code')} help={t('adm.tax.codeLocked')}>
              <ReadonlyValue mono>{draft.code}</ReadonlyValue>
            </Field>
          ) : (
            <Field label={t('adm.tax.code')} htmlFor="tax-code" required error={codeError} help={t('adm.tax.code.help')}>
              <input id="tax-code" className={codeError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(codeError)}
                     maxLength={32} value={draft.code} onChange={(e) => onChange({ ...draft, code: e.target.value.toLowerCase() })} />
            </Field>
          )}
          <Field label={t('adm.tax.name')} htmlFor="tax-name" required>
            <input id="tax-name" className="input" maxLength={128} value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('adm.tax.kind')} htmlFor="tax-kind">
            {frozen ? (
              <ReadonlyValue>{t(`adm.tax.kind.${draft.kind}`)}</ReadonlyValue>
            ) : (
              <select id="tax-kind" className="input" value={draft.kind} onChange={(e) => update({ kind: e.target.value as TaxRateKind })}>
                {kinds.map((kind) => <option key={kind} value={kind}>{t(`adm.tax.kind.${kind}`)}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('adm.tax.percent')} htmlFor="tax-percent" required>
            {frozen ? (
              <ReadonlyValue mono>{`${draft.percent} %`}</ReadonlyValue>
            ) : (
              <input id="tax-percent" className="input mono" inputMode="decimal" value={draft.percent}
                     onChange={(e) => update({ percent: e.target.value })} />
            )}
          </Field>
        </div>
        <Field label={t('adm.tax.estimated')} help={t('adm.tax.estimated.help')}>
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={draft.estimated} disabled={frozen}
                   onChange={(e) => update({ estimated: e.target.checked })} />
            <span>{t('adm.tax.estimated.label')}</span>
          </label>
        </Field>
        <Field label={t('adm.tax.ofd')} htmlFor="tax-ofd" required
               help={!frozen && Number.isFinite(percent) && matching.length === 0 ? t('adm.tax.ofd.none') : t('adm.tax.ofd.help')}>
          {frozen ? (
            <ReadonlyValue mono>{draft.ofdCode}</ReadonlyValue>
          ) : (
            <select id="tax-ofd" className="input mono" value={draft.ofdCode} disabled={matching.length === 0}
                    onChange={(e) => onChange({ ...draft, ofdCode: e.target.value })}>
              <option value="">{t('adm.tax.ofd.pick')}</option>
              {matching.map((code) => <option key={code.code} value={code.code}>{code.code}</option>)}
            </select>
          )}
        </Field>
        <div className="form-grid">
          <Field label={t('adm.tax.validFrom')} htmlFor="tax-from" required>
            {frozen ? (
              <ReadonlyValue mono>{draft.validFrom}</ReadonlyValue>
            ) : (
              <input id="tax-from" type="date" className="input" value={draft.validFrom} onChange={(e) => onChange({ ...draft, validFrom: e.target.value })} />
            )}
          </Field>
          <Field label={t('adm.tax.validTo')} htmlFor="tax-to" error={periodError} help={t('adm.tax.validTo.help')}>
            <input id="tax-to" type="date" className="input" value={draft.validTo} onChange={(e) => onChange({ ...draft, validTo: e.target.value })} />
          </Field>
        </div>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
